/**
 * CRM Status Mapper Utility
 * 
 * Maps CRM profile sanction and KYC status to delivery form status values
 */

/**
 * Map CRM profile sanction screening results to delivery form status
 * 
 * @param {Object} crmProfile - Full CRM profile object
 * @returns {string} - 'pending', 'cleared', or 'sanctioned'
 */
export const mapSanctionStatus = (crmProfile) => {
  if (!crmProfile) return 'pending';
  
  // Never screened
  if (!crmProfile.hk_last_screening_date) {
    return 'pending';
  }
  
  // Screened and suspicious
  if (crmProfile.hk_suspect_flag === true) {
    return 'sanctioned';
  }
  
  // Screened and clean
  return 'cleared';
};

/**
 * Map CRM profile KYC status to delivery form status
 * 
 * @param {Object} crmProfile - Full CRM profile object
 * @returns {string} - 'approved', 'pending', or 'flagged'
 */
export const mapKYCStatus = (crmProfile) => {
  if (!crmProfile || !crmProfile.kyc_status_current) {
    return 'pending';
  }
  
  // Direct mapping from CRM to lowercase
  const status = crmProfile.kyc_status_current.toLowerCase();
  
  // Validate the status is one of our expected values
  if (['approved', 'pending', 'flagged'].includes(status)) {
    return status;
  }
  
  // Default to pending if unknown status
  return 'pending';
};

/**
 * Tooltip shown on a grandfathered "Approved*" badge.
 */
export const GRANDFATHERED_APPROVAL_TOOLTIP =
  'Approved via the previous process — no KYC portal approval has been completed yet.';

/**
 * Derive how a partner's approval badge should render.
 *
 * A partner whose approval_status is 'approved' but which has never had a KYC
 * portal case approved (has_portal_kyc_approval === false) is grandfathered:
 * it shows "Approved*" with an explanatory tooltip. The asterisk disappears
 * automatically once a KYC case is approved. All other statuses render as-is.
 *
 * @param {Object} profile - CRM profile with approval_status + has_portal_kyc_approval
 * @returns {{ label: string, isGrandfathered: boolean, title: string, status: string }}
 */
export const getApprovalBadge = (profile) => {
  const status = (profile && profile.approval_status) || 'pending';
  const base = status.charAt(0).toUpperCase() + status.slice(1);
  const isGrandfathered =
    status === 'approved' && profile && profile.has_portal_kyc_approval === false;

  return {
    status,
    label: isGrandfathered ? `${base}*` : base,
    isGrandfathered,
    title: isGrandfathered ? GRANDFATHERED_APPROVAL_TOOLTIP : '',
  };
};

